import React, { useEffect, useState } from "react";
import "../../Styles/invoiceTable.css";
import { BsSearch } from "react-icons/bs";
import html2pdf from "html2pdf.js";
import TablePagination from "@mui/material/TablePagination";
import ReLoginModal from "../../components/ReLoginModal";
import * as XLSX from "xlsx";  // Import the xlsx library
import { CircularProgress } from "@mui/material";
import api from "../../util/axios";

type Requisition = {
  name: string;
  description: string;
  unitOfMeasure: string;
  category: string;
  price: any;
  quantity: string;
  _id: string;
  loadingSlip: any;
  customer: any;
  firstName: string;
  staff: any;
  business: string;
  items: any;
  businessType: string;
  requisitionRef: string;
  status: string;
  createdAt: string;
  mvtType: string;
};

const RequisitionReport = () => {
  const [getRequisition, setGetRequisition] = useState<Requisition[]>([]);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [page, setPage] = useState(0);
  const [reLogin, setReLogin] = useState(false);
  const token = window.localStorage.getItem("token");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const filterRequisitionsByDate = (requisitions: Requisition[]) => {
    return requisitions.filter((req) => {
      const reqDate = new Date(req.createdAt);
      const currentDate = new Date();

      const startDate = fromDate ? new Date(fromDate) : null;
      const endDate = toDate ? new Date(toDate) : null;
      if (endDate) {
        endDate.setHours(currentDate.getHours());
        endDate.setMinutes(currentDate.getMinutes());
        endDate.setSeconds(currentDate.getSeconds());
      }

      if (startDate && endDate) {
        return reqDate >= startDate && reqDate <= endDate;
      } else if (startDate) {
        return reqDate >= startDate;
      } else if (endDate) {
        return reqDate <= endDate;
      } else {
        return true; // No date range selected, so don't filter
      }
    });
  };

  const generatePDF = () => {
    const slipElement = document.querySelector(".invoiceTable");

    if (slipElement) {
      const pdfOptions = {
        margin: 2,
        filename: "Requisition-Report.pdf",
        image: { type: "png" },
        html2canvas: { scale: 2 },
        jsPDF: { unit: "mm", format: "a4", orientation: "landscape" },
      };
      html2pdf().from(slipElement).set(pdfOptions).save();
    }
  };

  const exportToExcel = () => {
    const filteredData = filterRequisitionsByDate(getRequisition)?.map((requisitions, inx) => {
      const createdAtDate = new Date(requisitions?.createdAt);
      const formattedDate = createdAtDate.toLocaleString();
      const proDetails = requisitions.items.map((pro: any) => pro);

      return {
        "S/N": inx + 1,
        "Business Name": requisitions?.customer?.businessName,
        "Product Name": proDetails[0].name,
        "Quantity": proDetails[0].quantity,
        "Unit Price": proDetails[0].unitPrice,
        "Discount": proDetails[0].discountedPrice,
        "Amount": proDetails[0].totalCost,
        "MVT Type": requisitions?.mvtType,
        "Created By": requisitions?.staff.firstName + " " + requisitions?.staff.lastName,
        "Date": formattedDate,
      };
    });

    const ws = XLSX.utils.json_to_sheet(filteredData);
    ws['!cols'] = [
      { width: 5 },
      { width: 25 },
      { width: 20 },
      { width: 15 },
      { width: 15 },
      { width: 15 },
      { width: 15 },
      { width: 15 },
      { width: 15 },
      { width: 20 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Requisitions");

    XLSX.writeFile(wb, "Requisition-Report.xlsx");
  };

  function queryRequisition(
    pageNumber: number,
    pageSize: number,
    fromDate: string,
    toDate: string
  ) {
    setIsLoading(true);
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    const queryParams = `?limit=${pageSize}&startDate=${fromDate}&endDate=${toDate}&page=${pageNumber}`;

    return api
      .get(`/requisition`, {
        headers: headers,
      })
      .then((res) => {
        setGetRequisition(res.data.data.requisitions);
      })
      .catch((error) => {
        if (
          error.response.data.message === "jwt expired" ||
          error.response.data.message === "jwt malformed"
        ) {
          setReLogin(true);
        } else {
          setReLogin(false);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  }

  useEffect(() => {
    queryRequisition(page + 1, rowsPerPage, fromDate, toDate);
  }, [page, rowsPerPage, fromDate, toDate]);

  // Search
  const [searchQuery, setSearchQuery] = useState("");
  const [searchField, setSearchField] = useState("businessName")
  const [businessQuery, setBusinessQuery] = useState("")
  const [productQuery, setProductQuery] = useState("")

  // Filter method

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <div className="board">
      {reLogin ? <ReLoginModal /> : null}
      <div className="method">
        <div className="searchFieldSelect">
          <label htmlFor="filter">Search filter: </label>
          <select
            id="filter"
            value={searchField}
            onChange={(e) => setSearchField(e.target.value)}
          >
            <option value="businessName">Customer Name</option>
            <option value="productName">Product Name</option>
            <option value="combined">Mixed search</option>
          </select>
        </div>
        {searchField === "combined" ? (
          <div className="combinedSearch">
            <input
              type="text"
              placeholder="Customer Name"
              value={businessQuery}
              onChange={(e) => setBusinessQuery(e.target.value)}
            />
            <input
              type="text"
              placeholder="Product Name"
              value={productQuery}
              onChange={(e) => setProductQuery(e.target.value)}
            />
          </div>
        ) : (
          <div className="apInput">
            <input
              type="text"
              value={searchQuery}
              placeholder="search"
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <BsSearch className="apIcon" />
          </div>
        )}
        <div className="filterBy">
          Filter By:
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />
        </div>
      </div>
      <div className="invoiceTable">
        <table className="inTable">
          <tr className="heTable">
            <th>S/N</th>
            <th>Customer Name</th>
            {/* <th>Description</th> */}
            <th>Product Name</th>
            <th>Quantity</th>
            <th className="smm">Unit Price</th>
            <th className="smm">Discount</th>
            <th className="smm">Amount</th>
            <th>MVT Type</th>
            <th>Created By</th>
            <th>Date</th>
          </tr>
          {isLoading ? (
            <div style={{ textAlign: "center", padding: "20px" }}>
              <CircularProgress />
              <h2>Please wait while we process the request...</h2>
            </div>
          ) : filterRequisitionsByDate(getRequisition)
            ?.filter((requisitions) => {
              if (searchField !== "combined" && !searchQuery) return true;
              if (searchField === "combined" && !businessQuery && !productQuery) return true;

              const businessNameMatch = requisitions?.customer?.businessName.toLowerCase().includes(searchField === "combined" ? businessQuery.toLowerCase() : searchQuery.toLowerCase())
              const productNames = requisitions?.items?.map((item: any) => item.name.toLowerCase()).join(' ');
              const productNameMatch = productNames.includes(searchField === "combined" ? productQuery.toLowerCase() : searchQuery.toLowerCase());

              switch (searchField) {
                case "businessName":
                  return businessNameMatch;
                case "productName":
                  return productNameMatch;
                case "combined":
                  return businessNameMatch && productNameMatch;
                default:
                  return businessNameMatch;
              }
            })
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            .map((requisitions, inx) => {
              const createdAtDate = new Date(requisitions?.createdAt);
              const formattedDate = createdAtDate.toLocaleDateString("en-US", {
                year: "numeric",
                month: "short",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
              });
              const proDetails = requisitions.items.map((pro: any) => pro);
              return (
                <tr key={inx} className="taData">
                  <td>{inx + 1}</td>
                  <td>{requisitions?.customer?.businessName}</td>
                  <td>{proDetails[0].name}</td>
                  <td>{proDetails[0].quantity}</td>
                  <td className="smm">{proDetails[0].unitPrice}</td>
                  <td className="smm">{proDetails[0].discountedPrice}</td>
                  <td className="smm">{proDetails[0].totalCost}</td>
                  <td>{requisitions?.mvtType}</td>
                  <td>
                    {requisitions?.staff.name}
                    {/* {requisitions?.staff.firstName +
                      " " +
                      requisitions?.staff.lastName} */}
                  </td>
                  <td>{formattedDate}</td>
                  <td></td>
                </tr>
              );
            })}
        </table>
      </div>

      <TablePagination
        rowsPerPageOptions={[15, 25, 50]}
        component="div"
        count={getRequisition.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      <div className="dowBtn">
        <button onClick={generatePDF}>Print Report</button>
        <button onClick={exportToExcel}>Export</button>
      </div>
    </div>
  );
};

export default RequisitionReport;
